import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { QUERY_OMS_ORDER_IDS } from './lookupQueries';
import {
    binLocationsLookupWhere,
    orderIdsFromLookup,
    orderVisibilityWhere,
    scanLookupWhere,
    searchLookupWheres,
} from './utils';

const NO_IDS = [];

const useOrderIdLookup = (lookup) => {
    const { data, loading, error } = useQuery(QUERY_OMS_ORDER_IDS, {
        variables: { where: lookup?.where || {}, limit: lookup?.limit ?? null },
        skip: !lookup,
    });
    const orderIds = useMemo(() => (lookup ? orderIdsFromLookup(data) : NO_IDS), [lookup, data]);
    return { orderIds, loading: !!lookup && loading, error: lookup ? error : undefined };
};

/**
 * Order ids for the My Orders filters that match on items or pallets: the search term's barcode and
 * bin/pool matches, a scanned label, and the picked bins and pools. Looking these up first keeps
 * item and pallet conditions out of the orders query, where they run once per order. Nothing is
 * looked up until the viewer's client ids are known, since every lookup is limited to their orders.
 *
 * @param {object} params
 * @param {string|null} params.searchTerm The search bar term.
 * @param {import('./utils').ScanTarget|null} params.scan The scanned label, if any.
 * @param {string[]} params.binLocations Picked bins and pools.
 * @param {string[]} params.clientIds The viewer's client ids.
 * @returns {{ searchOrderIds: string[], scanOrderIds: string[], binOrderIds: string[], loading: boolean,
 *   error: Error|undefined }} `error` is set when any lookup failed, so its ids are missing or stale.
 */
export const useOrderLookups = ({ searchTerm, scan, binLocations, clientIds }) => {
    const hasClients = !!clientIds?.length;
    const visibility = useMemo(() => orderVisibilityWhere(clientIds), [clientIds]);
    const search = useMemo(
        () => (hasClients ? searchLookupWheres(searchTerm, visibility) : null),
        [hasClients, searchTerm, visibility]
    );
    const scanType = scan?.type;
    const scanValue = scan?.value;
    const scanWarehouseId = scan?.warehouseId;
    const scanLookup = useMemo(
        () =>
            hasClients && scanType
                ? scanLookupWhere({ type: scanType, value: scanValue, warehouseId: scanWarehouseId }, visibility)
                : null,
        [hasClients, scanType, scanValue, scanWarehouseId, visibility]
    );
    const binLookup = useMemo(
        () => (hasClients && binLocations?.length ? binLocationsLookupWhere(binLocations, visibility) : null),
        [hasClients, binLocations, visibility]
    );

    const barcode = useOrderIdLookup(search?.barcode);
    const location = useOrderIdLookup(search?.location);
    const scanned = useOrderIdLookup(scanLookup);
    const bins = useOrderIdLookup(binLookup);

    const searchOrderIds = useMemo(
        () => [...new Set([...barcode.orderIds, ...location.orderIds])],
        [barcode.orderIds, location.orderIds]
    );

    return {
        searchOrderIds,
        scanOrderIds: scanned.orderIds,
        binOrderIds: bins.orderIds,
        loading: barcode.loading || location.loading || scanned.loading || bins.loading,
        error: barcode.error || location.error || scanned.error || bins.error,
    };
};
